import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { createLoadOptions } from "../../../hooks/useDebounce";
import { useI18n } from "../../../hooks/useTranslations";
import { useUsersApi } from "../../../http/users/api";
import { User } from "../../../http/users/types";
import SvgAdd from "../../icons/Add";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import FormFieldsContainer from "../../ui/FormFieldsContainer";
import ModalDialog from "../../ui/ModalDialog";
import reactSelectStyles from "../../ui/ReactSelectUtils";

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

interface CreateUserSubsidiaryDialogProps {
  subsidiaryId: string;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export interface UserSubsidiaryFormBody {
  user: User;
}

const CreateUserSubsidiaryDialog: FC<CreateUserSubsidiaryDialogProps> = ({ subsidiaryId, isDisabled, isLoading }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const usersApi = useUsersApi();
  const [show, setShow] = useState(false);

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);

  const onSubmit = async (formData: UserSubsidiaryFormBody) => {
    try {
      await usersApi.addAdminUserSubsidiary(subsidiaryId, formData.user.id);
      dispatch(showSuccessNotification(i18n.t));
      setShow(false);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  return (
    <>
      <Button buttonProps={{ onClick: () => setShow(true) }} glyph={SvgAdd} />
      {!!show && (
        <ModalDialog onClose={() => setShow(false)} title={i18n.t.userSubsidiaries.new}>
          <Form<UserSubsidiaryFormBody> submitText={i18n.t.common.save} onSubmit={() => onSubmit}>
            {({ control }) => {
              return (
                <FormFieldsContainer>
                  <Controller
                    name="user"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.users.singular} error={fieldState.error}>
                        {({ labelId, isInvalid }) => {
                          const searchTerm = useRef("");
                          const aborter = useRef(new AbortController());

                          const loadOptions = createLoadOptions<UserSearch, UserField>(
                            aborter,
                            searchTerm,
                            usersApi.getAdminUsers,
                            {
                              fields: USER_FIELDS,
                            },
                          );

                          // Fetch initial data on mount
                          useEffect(() => {
                            loadOptions(searchTerm.current).then(setDefaultUserOptions);
                          }, []);

                          return (
                            <AsyncSelect
                              {...field}
                              inputId={labelId}
                              isDisabled={isDisabled || isLoading}
                              isLoading={isLoading}
                              defaultOptions={defaultUserOptions}
                              loadOptions={loadOptions}
                              placeholder={`${i18n.t.common.select}...`}
                              getOptionLabel={(x) => x.fullName}
                              getOptionValue={(x) => x.id}
                              styles={reactSelectStyles<UserSearch>(isInvalid)}
                              menuPosition="fixed"
                            />
                          );
                        }}
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default CreateUserSubsidiaryDialog;
